import { get } from "http"

import ocularRequest, { ALTAR_BACKEND_URL } from "./request"
import ocularStreamingRequest from "./streaming-request"

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  messaging: {
    recordClipView(contentId, token = null) {
      const key = `clip-viewed:${contentId}`
      try {
        if (window.sessionStorage.getItem(key)) return Promise.resolve(null)
        window.sessionStorage.setItem(key, "1")
      } catch {}
      return ocularRequest("POST", `/email/content/${contentId}/viewed`, {}, {}, false, token)
    },
  },
  auth: {
    signup(details) {
      const path = `/auth/signup`
      return ocularRequest("POST", path, details)
    },
    signin(details) {
      const path = `/auth/signin`
      return ocularRequest("POST", path, details)
    },
    signout() {
      const path = `/auth/signout`
      return ocularRequest("POST", path)
    },
    sendVerificationEmail(details) {
      const path = `/auth/send-verification-email`
      return ocularRequest("POST", path, details)
    },
    verifyEmail(details) {
      const path = `/auth/verify-email`
      return ocularRequest("POST", path, details)
    },
    userCheck() {
      const path = `/auth/user-check`
      return ocularRequest("GET", path)
    },
  },
  sermon: {
    upload(workspaceId, sermonData) {
      const path = `workspaces/${workspaceId}/sermons`;
      return ocularRequest("POST", path, sermonData);
    },
    getSermon(workspaceId, sermonId) {
      const path = `workspaces/${workspaceId}/sermons/${sermonId}`;
      return ocularRequest("GET", path);
    },
    getAll(workspaceId) {
      const path = `workspaces/${workspaceId}/sermons`;
      return ocularRequest("GET", path);
    },
  },
  workspace: {
    fetchWorkspaces() {
      const path = `/workspaces`;
      return ocularRequest("GET", path);
    },
    fetchWorkspaceById(workspaceId) {
      const path = `/workspaces/${workspaceId}`;
      return ocularRequest("GET", path);
    },
    createWorkspace(workspaceData) {
      const path = `/workspaces`;
      return ocularRequest("POST", path, workspaceData);
    },
  },
  // Viral Clips API - New clip generation workflow endpoints
  clips: {
    /**
     * Get active processing contents for the current user
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to array of processing contents
     */
    getProcessingContent(token = null) {
      const path = `/clips/processing`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Get clip content status and clips
     * @param {string} contentId - Content UUID
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to content with clips array
     */
    getContent(contentId, token = null) {
      const path = `/clips/${contentId}`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * List all clips for a content
     * @param {string} contentId - Content UUID
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to array of clip summaries
     */
    listClips(contentId, token = null) {
      const path = `/clips/${contentId}/clips`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Get full clip details including words and captions
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to full clip data
     */
    getClip(contentId, clipId, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Update clip captions and styling
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} update - { caption_style, captions }
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to updated clip
     */
    updateClip(contentId, clipId, update, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}`;
      return ocularRequest("PUT", path, update, {}, false, token);
    },
    /**
     * Ask backend AI editor for editing_script operations
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { message?, editing_script, conversation_history?, enhance_options? }
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { type, content, edits }
     */
    aiEdit(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/ai/edit`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
    /**
     * Detect caption keywords and emoji with AI; the backend also stores them on the caption track
     * @returns {Promise} Promise resolving to { keywords: [{text, startMs, endMs}], emojis: [{emoji, startMs, endMs}] }
     */
    generateCaptionAI(contentId, clipId, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/captions/ai`;
      return ocularRequest("POST", path, {}, {}, false, token);
    },
    /**
     * Start a Remotion Lambda render
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} options - { editing_script, quality, format, generate_thumbnail, thumbnail_frame }
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { status, render_id, bucket_name, has_watermark }
     */
    remotionRender(contentId, clipId, options = {}, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/remotion-render`;
      return ocularRequest("POST", path, options, {}, false, token);
    },
    /**
     * Poll Remotion render progress
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {string} renderId - Remotion render ID
     * @param {string} bucketName - S3 bucket name
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { status, progress, frames_rendered, chunks, url, error }
     */
    remotionProgress(contentId, clipId, renderId, bucketName, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/remotion-progress`;
      return ocularRequest("GET", path, {}, { render_id: renderId, bucket_name: bucketName }, false, token);
    },
    /**
     * Delete a clip permanently
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { status, clip_id }
     */
    deleteClip(contentId, clipId, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}`;
      return ocularRequest("DELETE", path, {}, {}, false, token);
    },
    /**
     * Generate a thumbnail via Remotion Lambda
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} options - { editing_script, frame }
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { url, size_bytes, error }
     */
    remotionThumbnail(contentId, clipId, options = {}, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/remotion-thumbnail`;
      return ocularRequest("POST", path, options, {}, false, token);
    },
  },
  tools: {
    /**
     * Get all available tools from the backend (public endpoint)
     * @returns {Promise} Promise resolving to array of tool objects
     */
    getAllTools() {
      const path = `/public/tools`;
      return ocularRequest("GET", path);
    },
    /**
     * Get tool definition/landing page data for a specific tool
     * @param {string} toolName - The tool slug (e.g., 'watermark-remover')
     * @returns {Promise} Promise resolving to tool definition data
     */
    getToolDefinition(toolName) {
      const path = `/tools/public/tools/${toolName}/definition`;
      return ocularRequest("GET", path);
    },
    /**
     * Get all SEO tool configurations for landing pages (NEW)
     * @returns {Promise} Promise resolving to array of SEO tool configs
     * @example
     * const tools = await api.tools.getAllSEOTools()
     * // Returns 30+ tools with full SEO metadata
     */
    /**
     * Get the clip lengths the backend accepts, so the carousel cannot offer one it refuses.
     * @returns {Promise} Promise resolving to {min_seconds, max_seconds, default_seconds}
     */
    getClipDurationLimits() {
      const path = `/public/clip-lengths`;
      return ocularRequest("GET", path);
    },
    getAllSEOTools() {
      const path = `/public/tools/seo`;
      return ocularRequest("GET", path);
    },
    /**
     * Get SEO tool configuration by slug for landing page (NEW)
     * @param {string} slug - The tool slug (e.g., 'youtube-summary-generator')
     * @returns {Promise} Promise resolving to complete tool config
     * @example
     * const tool = await api.tools.getSEOToolBySlug('youtube-to-flashcards')
     * // Returns full config with keywords, examples, SEO metadata
     */
    getSEOToolBySlug(slug) {
      const path = `/public/tools/seo/${slug}`;
      return ocularRequest("GET", path);
    },
    /**
     * Get SEO tools by category (NEW)
     * @param {string} category - Category: 'basic', 'study', 'content', or 'analysis'
     * @returns {Promise} Promise resolving to array of tools in category
     */
    getSEOToolsByCategory(category) {
      const path = `/public/tools/seo/category/${category}`;
      return ocularRequest("GET", path);
    },
    /**
     * Get tool guide data with full pillar content for guide pages
     * @param {string} slug - The tool slug (e.g., 'youtube-summary-generator')
     * @returns {Promise} Promise resolving to tool config with pillar_content
     * @example
     * const guide = await api.tools.getGuideBySlug('youtube-to-flashcards')
     * // Returns tool config with pillar_content for deep SEO pages
     */
    getGuideBySlug(slug) {
      const path = `/public/tools/guide/${slug}`;
      return ocularRequest("GET", path);
    },
    /**
     * SSR-compatible: Get guide data using native fetch with Next.js caching
     * Use this in Server Components for guide pages
     * @param {string} slug - Tool slug
     * @returns {Promise<Object|null>} Tool data with pillar_content or null
     */
    async getGuideBySlugSSR(slug) {
      const response = await fetch(`${ALTAR_BACKEND_URL}/public/tools/guide/${slug}`, {
        next: { revalidate: 3600 }, // Cache for 1 hour
      });
      if (!response.ok) return null;
      return response.json();
    },
    /**
     * Get heuristic clip previews for a video
     * @param {string} videoId - YouTube video ID
     * @returns {Promise} Promise resolving to clip preview data
     */
    getClipPreviews(videoId) {
      return ocularRequest("GET", `/public/tools/clip-preview`, {}, { video_id: videoId });
    },
    /**
     * Stream AI-generated content from YouTube transcript using SSE
     * Start clip detection workflow (returns immediately, use Ably for progress).
     * @param {string} videoId - YouTube video ID
     * @param {Object} config - Detection config
     * @param {string} token - Auth token
     * @returns {Promise<{content_id: string, video_id: string, status: string}>}
     */
    async startClipDetection(videoId, config = {}, token = null) {
      if (!token) {
        throw new Error("Authentication required. Please sign up.");
      }
      const path = `/tools/stream`;
      const params = new URLSearchParams({
        video_id: videoId,
        tool_type: 'viral_clips',
        ...config,
      }).toString();
      return ocularRequest("GET", `${path}?${params}`, {}, {}, token);
    },

    /**
     * @param {string} videoId - YouTube video ID (11 characters)
     * @param {string} toolType - AI tool type (e.g., 'key_insights', 'summary')
     * @param {string} language - Output language (default: English)
     * @param {AbortController} cancelToken - Optional abort controller for cancellation
     * @param {string} token - Optional auth token for authenticated requests
     * @param {Object} config - Optional additional config (e.g., { output_format: "9:16" })
     * @returns {Promise<Response>} Fetch response with ReadableStream body
     * @example
     * const controller = new AbortController()
     * const response = await api.tools.streamToolGeneration('dQw4w9WgXcQ', 'key_insights', 'English', controller)
     * // Use with createReader() and readStream() from @/lib/stream
     */

    /**
     * Start clip detection workflow (returns immediately, use Ably for progress).
     * @param {string} videoId - YouTube video ID
     * @param {Object} config - Detection config
     * @param {string} token - Auth token
     * @returns {Promise<{content_id: string, video_id: string, status: string}>}
     */
    async startClipDetection(videoId, config = {}, token = null) {
      if (!token) {
        throw new Error("Authentication required. Please sign up.");
      }
      const path = `/tools/stream`;
      const params = {
        video_id: videoId,
        tool_type: 'viral_clips',
        ...config,
      };
      return ocularRequest("GET", path, {}, params, false, token);
    },

    streamToolGeneration(videoId, toolType, language = "English", cancelToken = null, token = null, config = {}) {
      if (!token) {
        throw new Error("Authentication required. Please sign up.")
      }
      const path = `/tools/stream`;
      return ocularStreamingRequest(
        "GET",
        path,
        {},
        { video_id: videoId, tool_type: toolType, language, ...config },
        cancelToken,
        token
      );
    },
  },
  transcript: {
    /**
     * Get YouTube video transcript (public endpoint - no authentication required)
     * @param {string} url - YouTube video URL
     * @param {string} language - Optional language code (e.g., 'en', 'es', 'fr')
     * @returns {Promise} Promise resolving to transcript data with video info and snippets
     * @example
     * api.transcript.getTranscript('https://www.youtube.com/watch?v=dQw4w9WgXcQ')
     * api.transcript.getTranscript('https://www.youtube.com/watch?v=dQw4w9WgXcQ', 'es')
     */
    getTranscript(url, language = undefined) {
      const path = `/public/transcript`;
      const params = { url };
      if (language) params.language = language;
      return ocularRequest("GET", path, {}, params);
    },
    /**
     * Get YouTube video metadata (duration, title, channel, thumbnail)
     * independent of transcript availability. Backed by YouTube Data API v3
     * with yt-dlp fallback. PUBLIC endpoint — rate-limited (30/min;300/day).
     * Used by SEO landing pages (Viral Clips Finder etc.) and the
     * authenticated configure flow.
     * @param {string} url - YouTube video URL
     * @returns {Promise<{data: {duration_seconds: number, title: string, channel: string|null, thumbnail_url: string|null, video_id: string}}>}
     */
    getVideoMetadata(url) {
      const path = `/public/video-metadata`;
      return ocularRequest("GET", path, {}, { url });
    },
  },
  videos: {
    /**
     * List all videos for the current user
     * @param {Object} params - Query parameters (page, limit, search, favorites_only)
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to paginated video list
     */
    list(params = {}, token = null) {
      const path = `/videos`;
      return ocularRequest("GET", path, {}, params, false, token);
    },
    /**
     * Authenticated metadata lookup — same shape as
     * api.transcript.getVideoMetadata() but uses the auth-gated
     * /videos/metadata route so paying users don't share the public
     * rate-limit bucket. Use this from inside (dashboard) pages.
     * @param {string} url - YouTube video URL
     * @param {string} token - Clerk auth token
     * @returns {Promise<{data: {duration_seconds: number, title: string, channel: string|null, thumbnail_url: string|null, video_id: string}}>}
     */
    getMetadata(url, token = null) {
      const path = `/videos/metadata`;
      return ocularRequest("GET", path, {}, { url }, false, token);
    },
    /**
     * Get a single video with all generated content
     * @param {string} videoId - Video UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to video with content
     */
    get(videoId, token = null) {
      const path = `/videos/${videoId}`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Check if a video exists in user's library
     * @param {string} youtubeVideoId - YouTube video ID (11 chars)
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to { exists: boolean, video_id: string|null }
     */
    async checkExists(youtubeVideoId, token = null) {
      const response = await ocularRequest("GET", "/videos", {}, { youtube_video_id: youtubeVideoId, items_per_page: 1 }, false, token);
      const video = (response.data || response).items?.[0];
      return { exists: Boolean(video), video_id: video?.id ?? null };
    },
    /**
     * Create or update a video in user's library
     * @param {Object} videoData - Video data (url, youtube_video_id, title, etc.)
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to created/updated video
     */
    create(videoData, token = null) {
      const path = `/videos`;
      return ocularRequest("POST", path, videoData, {}, false, token);
    },
    /**
     * Update video (favorite, title)
     * @param {string} videoId - Video UUID
     * @param {Object} update - Update data { title?, is_favorite? }
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to updated video
     */
    update(videoId, update, token = null) {
      const path = `/videos/${videoId}`;
      return ocularRequest("PATCH", path, update, {}, false, token);
    },
    /**
     * Delete a video from user's library
     * @param {string} videoId - Video UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving when deleted
     */
    delete(videoId, token = null) {
      const path = `/videos/${videoId}`;
      return ocularRequest("DELETE", path, {}, {}, false, token);
    },
  },
  content: {
    /**
     * List all generated content for a video
     * @param {string} videoId - Video UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to array of generated content
     */
    list(videoId, token = null) {
      const path = `/videos/${videoId}/content`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Get specific content by tool type
     * @param {string} videoId - Video UUID
     * @param {string} toolType - AI tool type
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to generated content
     */
    get(videoId, toolType, token = null) {
      const path = `/videos/${videoId}/content/${toolType}`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Save generated content (upsert)
     * @param {string} videoId - Video UUID
     * @param {Object} contentData - { tool_type, content, raw_content, language, etc. }
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to saved content
     */
    save(videoId, contentData, token = null) {
      const path = `/videos/${videoId}/content`;
      return ocularRequest("POST", path, contentData, {}, false, token);
    },
    /**
     * Delete specific content by tool type
     * @param {string} videoId - Video UUID
     * @param {string} toolType - AI tool type
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving when deleted
     */
    delete(videoId, toolType, token = null) {
      const path = `/videos/${videoId}/content/${toolType}`;
      return ocularRequest("DELETE", path, {}, {}, false, token);
    },
    /**
     * Delete all content for a video
     * @param {string} videoId - Video UUID
     * @returns {Promise} Promise resolving when deleted
     */
    deleteAll(videoId) {
      const path = `/videos/${videoId}/content`;
      return ocularRequest("DELETE", path);
    },
  },
  pages: {
    /** Gets a public page by slug for SSR; null when missing. */
    async getBySlugSSR(slug) {
      const response = await fetch(`${ALTAR_BACKEND_URL}/public/pages/${slug}`, {
        next: { revalidate: 3600 },
      });
      if (!response.ok) return null;
      return response.json();
    },
    /** Lists other pages for the same video, for internal linking. */
    async getRelatedSSR(youtubeVideoId, excludeSlug = null, limit = 10) {
      const params = new URLSearchParams({ youtube_video_id: youtubeVideoId, items_per_page: String(limit) });
      if (excludeSlug) params.append("exclude_slug", excludeSlug);
      const response = await fetch(`${ALTAR_BACKEND_URL}/public/pages?${params}`, {
        next: { revalidate: 3600 },
      });
      if (!response.ok) return [];
      return (await response.json()).items;
    },
  },
  billing: {
    /**
     * Get all pricing plans (public endpoint - no authentication required)
     * @returns {Promise} Promise resolving to { plans: PricingPlan[] }
     * @example
     * const { plans } = await api.billing.getPricingPlans()
     * // Returns: [{ id: 'creator_plan', name: 'Creator', price: 12.99, ... }, ...]
     */
    getPricingPlans() {
      const path = `/public/usage/pricing`;
      return ocularRequest("GET", path, {}, {}, false, null);
    },
    /**
     * Create checkout session for plan upgrade (requires authentication)
     * @param {string} planId - Plan identifier ('creator_plan', 'pro_plan', 'business_plan')
     * @param {string} successUrl - URL to redirect after successful payment
     * @param {string} token - Clerk auth token
     * @param {Object} metadata - Optional metadata for Stripe (e.g., Datafast attribution)
     * @returns {Promise} Promise resolving to { checkout_url, session_id } or { error }
     * @example
     * import { getDatafastMetadata } from '@/lib/datafast'
     * const result = await api.billing.createCheckout('pro_plan', window.location.href, token, getDatafastMetadata())
     * if (result.checkout_url) window.location.href = result.checkout_url
     */
    createCheckout(planId, successUrl, token, metadata = {}, billingPeriod = "month", promoCode = null, countryCode = null) {
      const path = `/usage/checkout`;
      const body = { plan_id: planId, success_url: successUrl, metadata, billing_period: billingPeriod };
      if (promoCode) body.promo_code = promoCode;
      if (countryCode) body.country_code = countryCode;
      return ocularRequest("POST", path, body, {}, false, token);
    },
    getBillingInfo(token, { refresh = false } = {}) {
      const path = refresh ? `/usage/info?refresh=true` : `/usage/info`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    previewCheckout(token, planId, promoCode = null, billingPeriod = "month", countryCode = null) {
      const path = `/usage/preview-checkout`;
      const body = { plan_id: planId, billing_period: billingPeriod };
      if (promoCode) body.promo_code = promoCode;
      if (countryCode) body.country_code = countryCode;
      return ocularRequest("POST", path, body, {}, false, token);
    },
    cancelSubscription(token) {
      const path = `/usage/cancel-subscription`;
      return ocularRequest("POST", path, {}, {}, false, token);
    },
    cancelScheduledChange(token) {
      const path = `/usage/cancel-scheduled-change`;
      return ocularRequest("POST", path, {}, {}, false, token);
    },
    /**
     * Get usage history (requires authentication)
     * @param {string} range - Time range ('7d', '30d', '90d')
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to { usage: UsageHistoryEntry[] }
     * @example
     * const { usage } = await api.billing.getUsageHistory('30d', token)
     */
    getUsageHistory(range = "30d", token) {
      const path = `/usage/usage-history`;
      return ocularRequest("GET", path, {}, { range }, false, token);
    },
  },
  // B-Roll Generation API - AI and Stock B-Roll for clips
  broll: {
    /**
     * Start AI B-roll generation using DALL-E
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { start_ms, end_ms, transcript_context, style? }
     * @param {number} data.start_ms - Start time in milliseconds
     * @param {number} data.end_ms - End time in milliseconds
     * @param {string} data.transcript_context - Transcript text or custom prompt
     * @param {string} [data.style] - Style preset: "photo-realistic", "pop-art", "surreal"
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { task_id, status }
     */
    generateAI(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/brolls/generate-ai`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
    /**
     * Generate search keyword for stock B-roll from transcript
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { start_ms, end_ms, transcript_context }
     * @param {number} data.start_ms - Start time in milliseconds
     * @param {number} data.end_ms - End time in milliseconds
     * @param {string} data.transcript_context - Transcript context
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { keyword, alternative_keywords, visual_description }
     */
    generateKeyword(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/brolls/generate-keyword`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
    /**
     * Search stock footage with keyword
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { start_ms, end_ms, search_query }
     * @param {number} data.start_ms - Start time in milliseconds
     * @param {number} data.end_ms - End time in milliseconds
     * @param {string} data.search_query - Search keyword
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { task_id, status }
     */
    searchStock(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/brolls/search-stock`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
    /**
     * Get B-roll task status (for polling)
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {string} taskId - Task ID
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to task status with result
     */
    getTaskStatus(contentId, clipId, taskId, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/brolls/tasks/${taskId}`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Apply selected B-roll to clip timeline
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { start_ms, end_ms, broll_type, media_info }
     * @param {number} data.start_ms - Start time in milliseconds
     * @param {number} data.end_ms - End time in milliseconds
     * @param {string} data.broll_type - "GenAi" or "Stock"
     * @param {Object} data.media_info - Media information object
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { status, editing_script }
     */
    apply(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/brolls/apply`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
  },
  // Voiceover Generation API - AI voiceover narration for clips
  voiceover: {
    /**
     * List available ElevenLabs voices
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { voices, default_voice_id, default_voice_name, available_tones }
     */
    listVoices(token = null) {
      const path = `/clips/voices`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Generate voiceover narration for a clip (hook and/or CTA)
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { voice_id, voiceover_types, tone, cta_type, volume, duck_original_audio, duck_level }
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { voiceovers, clip_id, total_duration_ms }
     */
    generate(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/voiceover/generate`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
    /**
     * Regenerate a voiceover segment with different wording
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { segment_id, voiceover_type, voice_id?, tone? }
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to new VoiceoverSegmentData
     */
    regenerate(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/voiceover/regenerate`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
    /**
     * Remove a voiceover segment from clip
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {string} segmentId - Segment ID to remove
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { status, clip_id, segment_id }
     */
    remove(contentId, clipId, segmentId, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/voiceover/${segmentId}`;
      return ocularRequest("DELETE", path, {}, {}, false, token);
    },
  },
  social: {
    /**
     * Get available social media platforms for connection
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to array of platform objects
     * @example
     * const platforms = await api.social.getPlatforms(token)
     * // Returns: [{ slug: 'youtube', title: 'YouTube', icon_url: '...', ... }, ...]
     */
    getPlatforms(token) {
      const path = `/social/platforms`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Get OAuth authorization URL for a platform
     * @param {string} platform - Platform slug ('youtube', 'tiktok', 'instagram')
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to { auth_url, state, redirect_uri }
     * @example
     * const { auth_url, state } = await api.social.getAuthUrl('youtube', token)
     * sessionStorage.setItem('oauth_state', state)
     * window.location.href = auth_url
     */
    getAuthUrl(platform, token) {
      const path = `/social/${platform}/auth`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Handle OAuth callback after platform authorization
     * @param {string} platform - Platform slug
     * @param {string} code - Authorization code from OAuth callback
     * @param {string} state - State parameter for CSRF verification
     * @param {string} redirectUri - Redirect URI used in authorization
     * @param {string} token - Clerk auth token
     * @param {string} [codeVerifier] - PKCE code_verifier returned by /auth
     *   (TikTok only; round-tripped from sessionStorage). Omit for platforms
     *   that don't require PKCE (YouTube, Instagram).
     * @returns {Promise} Promise resolving to connected account data
     */
    handleCallback(platform, code, state, redirectUri, token, codeVerifier) {
      const path = `/social/${platform}/callback`;
      const query = { code, state, redirect_uri: redirectUri };
      if (codeVerifier) {
        query.code_verifier = codeVerifier;
      }
      return ocularRequest("POST", path, {}, query, false, token);
    },
    /**
     * List all connected social media accounts
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to array of connected accounts
     * @example
     * const accounts = await api.social.getAccounts(token)
     * // Returns: [{ id: 'uuid', slug: 'youtube', display_name: 'My Channel', ... }, ...]
     */
    getAccounts(token) {
      const path = `/social/accounts`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Get a single connected account
     * @param {string} accountId - Account UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to account data
     */
    getAccount(accountId, token) {
      const path = `/social/accounts/${accountId}`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Update a connected account (enable/disable, rename)
     * @param {string} accountId - Account UUID
     * @param {Object} data - Update data { enabled?, display_name? }
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to updated account
     */
    updateAccount(accountId, data, token) {
      const path = `/social/accounts/${accountId}`;
      return ocularRequest("PATCH", path, data, {}, false, token);
    },
    /**
     * Disconnect a social media account
     * @param {string} accountId - Account UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving when deleted
     */
    disconnectAccount(accountId, token) {
      const path = `/social/accounts/${accountId}`;
      return ocularRequest("DELETE", path, {}, {}, false, token);
    },
    /**
     * Force refresh OAuth tokens for an account
     * @param {string} accountId - Account UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to updated account
     */
    refreshAccount(accountId, token) {
      const path = `/social/accounts/${accountId}/refresh`;
      return ocularRequest("POST", path, {}, {}, false, token);
    },
    /**
     * Test if account credentials are still valid
     * @param {string} accountId - Account UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to { account_id, slug, is_valid }
     */
    testAccount(accountId, token) {
      const path = `/social/accounts/${accountId}/test`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Publish a video to a connected social media account
     * @param {string} accountId - Account UUID (YouTube, TikTok, or Instagram account)
     * @param {Object} publishData - Video publish data
     * @param {string} publishData.video_url - S3 URL of the video to publish
     * @param {string} publishData.title - Video title (max 100 chars)
     * @param {string} publishData.description - Video description (optional)
     * @param {string[]} publishData.tags - List of tags (optional)
     * @param {string} publishData.privacy - Privacy status: 'public', 'unlisted', 'private' (default: 'public')
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to { status, video_id, url, title, privacy_status, error_message }
     * @example
     * const result = await api.social.publishVideo(accountId, {
     *   video_url: 'https://s3.amazonaws.com/bucket/video.mp4',
     *   title: 'My YouTube Short',
     *   description: 'Check out this clip!',
     *   tags: ['shorts', 'viral'],
     *   privacy: 'public'
     * }, token)
     * if (result.status === 'success') {
     *   console.log('Published:', result.url)
     * }
     */
    publishVideo(accountId, publishData, token) {
      const path = `/social/accounts/${accountId}/publish`;
      return ocularRequest("POST", path, publishData, {}, false, token);
    },
    /**
     * Fetch TikTok creator_info for the publish UI (privacy options,
     * interaction locks, max video duration). Required by TikTok's Content
     * Posting API audit before showing the publish form.
     * @param {string} accountId - TikTok account UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to {
     *   creator_username, creator_nickname, creator_avatar_url,
     *   privacy_level_options, comment_disabled, duet_disabled, stitch_disabled,
     *   max_video_post_duration_sec
     * }
     */
    getTiktokCreatorInfo(accountId, token) {
      const path = `/social/tiktok/${accountId}/creator-info`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
  },
  // Thumbnail Generation API - AI thumbnails for clips
  thumbnail: {
    /**
     * Generate AI thumbnails for a clip
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { style?, template_id?, title_override?, has_subject_override?, count?, frame_data?, edit_prompt? }
     * @param {string} [data.style] - "auto", "template", or "frame_edit"
     * @param {string} [data.template_id] - Specific template ID (when style="template")
     * @param {string} [data.title_override] - Custom title for thumbnail
     * @param {boolean} [data.has_subject_override] - Override face detection
     * @param {number} [data.count] - Number of thumbnails to generate (default 3)
     * @param {string} [data.frame_data] - Base64 encoded frame image (required for style="frame_edit")
     * @param {string} [data.edit_prompt] - Custom prompt for frame editing (optional)
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { thumbnails, clip_id, video_summary, detected_has_subject, genre, subgenre }
     */
    generate(contentId, clipId, data = {}, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/thumbnails/generate`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
    /**
     * Save a thumbnail to a clip
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { thumbnail_url, source, time_ms? }
     * @param {string} data.thumbnail_url - URL of thumbnail to save
     * @param {string} data.source - "ai", "frame", or "upload"
     * @param {number} [data.time_ms] - Time in ms for frame thumbnails
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { thumbnail_url, clip_id, source }
     */
    save(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/thumbnail`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
  },
  // Music API - Background music search and management for clips
  music: {
    /** Lists catalog tracks; the music panel reads { tracks, total }. */
    search(contentId, clipId, data, token = null) {
      const limit = data.limit || 20;
      const params = { items_per_page: limit, page: Math.floor((data.offset || 0) / limit) + 1 };
      if (data.query) params.q = data.query;
      if (data.mood || data.style) params.style = data.mood || data.style;
      return ocularRequest("GET", "/music-catalog/tracks", {}, params, false, token).then(
        (response) => ({ data: { tracks: response.data.items, total: response.data.total } })
      );
    },
    /**
     * Apply music track to clip timeline
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { start_ms, end_ms, media_info, audio_settings? }
     * @param {number} data.start_ms - Start time in milliseconds
     * @param {number} data.end_ms - End time in milliseconds
     * @param {Object} data.media_info - Music track info from search
     * @param {Object} [data.audio_settings] - Optional audio settings
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { status, editing_script }
     */
    apply(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/music/apply`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
    /**
     * Remove music segment from clip timeline
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {string} segmentId - Music segment ID to remove
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { status, editing_script }
     */
    remove(contentId, clipId, segmentId, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/music/${segmentId}`;
      return ocularRequest("DELETE", path, {}, {}, false, token);
    },
    /**
     * Update music segment settings (volume, fade, ducking)
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {string} segmentId - Music segment ID
     * @param {Object} data - { volume?, fadeInMs?, fadeOutMs?, duckUnderSpeech?, duckLevel? }
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { status, editing_script }
     */
    update(contentId, clipId, segmentId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/music/${segmentId}`;
      return ocularRequest("PATCH", path, data, {}, false, token);
    },
  },
  // GIF Catalog API - Search and browse enriched Vlipsy GIF clips
  gifs: {
    list(params, token = null) {
      return ocularRequest("GET", "/gifs", {}, params, false, token);
    },
    categories(token = null) {
      const path = `/gifs/categories`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    getById(clipId, token = null) {
      const path = `/gifs/${clipId}`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /** Signs one clip's video for a few minutes; lists carry thumbnails only. */
    video(clipId, token = null) {
      return ocularRequest("GET", `/gifs/${clipId}/video`, {}, {}, false, token);
    },
    /**
     * Apply a GIF clip to the editing timeline
     * @param {string} contentId - Content UUID
     * @param {string} clipId - Clip UUID
     * @param {Object} data - { start_ms, end_ms, media_info, audio_settings }
     * @param {string} token - Auth token
     * @returns {Promise} Promise resolving to { status, clip_id, editing_script }
     */
    apply(contentId, clipId, data, token = null) {
      const path = `/clips/${contentId}/clips/${clipId}/gif/apply`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
  },
  // Workflow Automation API - Visual workflow builder for automated video processing
  workflows: {
    /**
     * Get all available node types for the workflow editor
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to { node_types: NodeTypeInfo[] }
     * @example
     * const { node_types } = await api.workflows.getNodeTypes(token)
     * // Returns: [{ type: 'detect_clips', name: 'Detect Clips', category: 'detection', ... }, ...]
     */
    getNodeTypes(token) {
      const path = `/workflows/node-types`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * List all workflows for the current user
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to array of workflows
     * @example
     * const workflows = await api.workflows.list(token)
     * // Returns: [{ id: 'uuid', name: 'My Workflow', is_active: true, ... }, ...]
     */
    list(token) {
      const path = `/workflows/`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Create a new workflow
     * @param {Object} data - { name, description? }
     * @param {string} data.name - Workflow name
     * @param {string} [data.description] - Optional description
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to created workflow
     */
    create(data, token) {
      const path = `/workflows/`;
      return ocularRequest("POST", path, data, {}, false, token);
    },
    /**
     * Get a workflow with its nodes and edges
     * @param {string} workflowId - Workflow UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to workflow with nodes and edges
     */
    get(workflowId, token) {
      const path = `/workflows/${workflowId}`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
    /**
     * Update a workflow (name, description, nodes, edges)
     * @param {string} workflowId - Workflow UUID
     * @param {Object} data - { name?, description?, nodes?, edges? }
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to updated workflow
     */
    update(workflowId, data, token) {
      const path = `/workflows/${workflowId}`;
      return ocularRequest("PUT", path, data, {}, false, token);
    },
    /**
     * Delete a workflow
     * @param {string} workflowId - Workflow UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving when deleted
     */
    delete(workflowId, token) {
      const path = `/workflows/${workflowId}`;
      return ocularRequest("DELETE", path, {}, {}, false, token);
    },
    /**
     * Activate a workflow (enable triggers)
     * @param {string} workflowId - Workflow UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to { id, is_active, message }
     */
    activate(workflowId, token) {
      const path = `/workflows/${workflowId}/activate`;
      return ocularRequest("POST", path, {}, {}, false, token);
    },
    /**
     * Deactivate a workflow (disable triggers)
     * @param {string} workflowId - Workflow UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to { id, is_active, message }
     */
    deactivate(workflowId, token) {
      const path = `/workflows/${workflowId}/deactivate`;
      return ocularRequest("POST", path, {}, {}, false, token);
    },
    /**
     * Manually execute a workflow
     * @param {string} workflowId - Workflow UUID
     * @param {Object} triggerData - Data to pass to the workflow
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to { execution_id, status, message }
     */
    execute(workflowId, triggerData = {}, token) {
      const path = `/workflows/${workflowId}/execute`;
      return ocularRequest("POST", path, { trigger_data: triggerData }, {}, false, token);
    },
    /**
     * List executions for a workflow
     * @param {string} workflowId - Workflow UUID
     * @param {Object} params - { limit?, offset? }
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to array of executions
     */
    listExecutions(workflowId, params = {}, token) {
      const path = `/workflows/${workflowId}/executions`;
      return ocularRequest("GET", path, {}, params, false, token);
    },
    /**
     * Get execution details with node-level status
     * @param {string} executionId - Execution UUID
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to execution with node_executions
     */
    getExecution(executionId, token) {
      const path = `/workflows/executions/${executionId}`;
      return ocularRequest("GET", path, {}, {}, false, token);
    },
  },
  brand: {
    list(token) {
      return ocularRequest("GET", "/brand-kits", null, {}, false, token);
    },
    create(data, token) {
      return ocularRequest("POST", "/brand-kits", data, {}, false, token);
    },
    get(id, token) {
      return ocularRequest("GET", `/brand-kits/${id}`, null, {}, false, token);
    },
    update(id, data, token) {
      return ocularRequest("PUT", `/brand-kits/${id}`, data, {}, false, token);
    },
    remove(id, token) {
      return ocularRequest("DELETE", `/brand-kits/${id}`, null, {}, false, token);
    },
    uploadLogo(file, token) {
      const formData = new FormData();
      formData.append("file", file);
      return ocularRequest("POST", "/brand-kits/upload-logo", formData, {}, false, token);
    },
  },
  // In-App Feedback API
  feedback: {
    /**
     * Submit feedback from the in-app widget (authenticated)
     * @param {Object} data - { feedback_type, title, description, page_url, client_metadata }
     * @param {string} token - Clerk auth token
     * @returns {Promise} Promise resolving to FeedbackRead
     */
    submit(data, token) {
      return ocularRequest("POST", "/feedback", data, {}, false, token);
    },
  },
  // Admin Feedback API
  admin: {
    feedback: {
      /**
       * List feedback items with filters and pagination (admin only)
       * @param {Object} params - { page, limit, status, feedback_type, search, sort, desc }
       * @param {string} token - Clerk auth token
       * @returns {Promise} Promise resolving to FeedbackListResponse
       */
      list(params = {}, token) {
        return ocularRequest("GET", "/admin/feedback", {}, params, false, token);
      },
      /**
       * Get a single feedback item by ID (admin only)
       * @param {string} id - Feedback UUID
       * @param {string} token - Clerk auth token
       * @returns {Promise} Promise resolving to FeedbackRead
       */
      get(id, token) {
        return ocularRequest("GET", `/admin/feedback/${id}`, {}, {}, false, token);
      },
      /**
       * Update feedback status and/or admin notes (admin only)
       * @param {string} id - Feedback UUID
       * @param {Object} data - { status?, admin_notes? }
       * @param {string} token - Clerk auth token
       * @returns {Promise} Promise resolving to updated FeedbackRead
       */
      update(id, data, token) {
        return ocularRequest("PATCH", `/admin/feedback/${id}`, data, {}, false, token);
      },
    },
  },
  // Content Queue API — Scheduled posts for Kanban + Calendar
  post: {
    list(params = {}, token) {
      return ocularRequest("GET", "/post/", {}, params, false, token);
    },
    getKanban(token) {
      return ocularRequest("GET", "/post/kanban", {}, {}, false, token);
    },
    getCalendar(start, end, token) {
      return ocularRequest("GET", "/post/calendar", {}, { start, end }, false, token);
    },
    get(postId, token) {
      return ocularRequest("GET", `/post/${postId}`, {}, {}, false, token);
    },
    create(data, token) {
      return ocularRequest("POST", "/post/", data, {}, false, token);
    },
    bulkCreate(data, token) {
      return ocularRequest("POST", "/post/bulk", data, {}, false, token);
    },
    update(postId, data, token) {
      return ocularRequest("PATCH", `/post/${postId}`, data, {}, false, token);
    },
    delete(postId, token) {
      return ocularRequest("DELETE", `/post/${postId}`, {}, {}, false, token);
    },
    retry(postId, token) {
      return ocularRequest("POST", `/post/${postId}/retry`, {}, {}, false, token);
    },
  },
  research: {
    listSessions(token) {
      return ocularRequest("GET", "/research/sessions", {}, {}, false, token);
    },
    getSession(sessionId, token) {
      return ocularRequest("GET", `/research/sessions/${sessionId}`, {}, {}, false, token);
    },
    createSession(data, token) {
      return ocularRequest("POST", "/research/sessions", data, {}, false, token);
    },
    parseBrief(brief, token) {
      return ocularRequest("POST", "/research/brief", { brief }, {}, false, token);
    },
    updateSession(sessionId, data, token) {
      return ocularRequest("PATCH", `/research/sessions/${sessionId}`, data, {}, false, token);
    },
    getFindings(sessionId, includeDismissed, token) {
      return ocularRequest(
        "GET",
        `/research/sessions/${sessionId}/findings`,
        {},
        { include_dismissed: includeDismissed },
        false,
        token
      );
    },
    updateFinding(sessionId, findingId, data, token) {
      return ocularRequest(
        "PATCH",
        `/research/sessions/${sessionId}/findings/${findingId}`,
        data,
        {},
        false,
        token
      );
    },
    analyzeFinding(sessionId, findingId, token) {
      return ocularRequest(
        "POST",
        `/research/sessions/${sessionId}/findings/${findingId}/analyze`,
        {},
        {},
        false,
        token
      );
    },
  },
}
